import { COLORS } from './constants';

// ── Brand Colors ─────────────────────────────────────────────────────────
const C = {
  ...COLORS,
  // Ensure white is available
  white: [255, 255, 255],
};

// ── Number formatting (use \u00A0 non-breaking space to avoid jsPDF "/" bug) ─
const NBSP = '\u00A0';

const fmtEur = (v) => {
  if (v == null || isNaN(v)) return '-';
  const num = Number(v);
  const parts = num.toFixed(1).split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${intPart},${parts[1]}${NBSP}\u20AC`;
};

const fmtNum = (v) => {
  if (v == null || v === '' || isNaN(v)) return '-';
  return Math.round(Number(v)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
};

// ── Constants ────────────────────────────────────────────────────────────
const PERIODS = ['pointe', 'hph', 'hch', 'hpe', 'hce'];
const PERIOD_LABELS = ['Pointe', 'HPH', 'HCH', 'HPE', 'HCE'];

const COST_ROWS = [
  ['Composante de comptage', 'comptage'],
  ["Composante de l'\u00E9nergie r\u00E9active", 'energie_reactive'],
  ['Composante de gestion', 'gestion'],
  ['Composante mensuelle des d\u00E9passements', 'depassements'],
  ['Composante de soutirage - part fixe', 'soutirage_fixe'],
  ['Composante de soutirage - part variable', 'soutirage_variable'],
  ["Contribution Tarifaire d'Acheminement", 'cta'],
];

// ── Layout constants ─────────────────────────────────────────────────────
const M = 18;       // margin
const W = 210;      // A4 width
const H = 297;      // A4 height
const RIGHT = W - M;
const CONTENT_W = RIGHT - M;
const FOOTER_Y = H - 12;
const MAX_Y = FOOTER_Y - 8; // max Y before forcing new page

// ── Logo loader ──────────────────────────────────────────────────────────
let cachedLogo = null;
const loadLogo = async () => {
  if (cachedLogo) return cachedLogo;
  try {
    const res = await fetch('/logo-pde-2025.png');
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => { cachedLogo = reader.result; resolve(cachedLogo); };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
};

// ── Font setup ───────────────────────────────────────────────────────────
let fontFamily = 'helvetica';

const setupFont = async (doc) => {
  try {
    const { karlaRegular, karlaBold } = await import('../../fonts/karlaFont');
    if (karlaRegular) {
      doc.addFileToVFS('Karla-Regular.ttf', karlaRegular);
      doc.addFont('Karla-Regular.ttf', 'Karla', 'normal');
    }
    if (karlaBold) {
      doc.addFileToVFS('Karla-Bold.ttf', karlaBold);
      doc.addFont('Karla-Bold.ttf', 'Karla', 'bold');
    }
    if (doc.getFontList()['Karla']) fontFamily = 'Karla';
  } catch { /* fallback helvetica */ }
  doc.setFont(fontFamily, 'normal');
};

// ── Page helpers ─────────────────────────────────────────────────────────
const drawPageHeader = (doc, logoBase64) => {
  // Logo top-left — original ratio 3.93:1 (444x113px)
  if (logoBase64) {
    try {
      const logoH = 9;
      const logoW = logoH * 3.93; // ≈ 35mm
      doc.addImage(logoBase64, 'PNG', M, 10, logoW, logoH, undefined, 'FAST');
    } catch { /* skip */ }
  }
  // Thin blue accent line
  doc.setDrawColor(...C.azur);
  doc.setLineWidth(0.8);
  doc.line(M, 22, RIGHT, 22);
  // Title below line — with breathing room
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text("PROPOSITION D'OPTIMISATION DE PUISSANCE SOUSCRITE", W / 2, 29, { align: 'center' });
};

const drawPageFooter = (doc, pageNum, totalPages, contact) => {
  doc.setDrawColor(...C.argent);
  doc.setLineWidth(0.25);
  doc.line(M, FOOTER_Y - 4, RIGHT, FOOTER_Y - 4);
  doc.setTextColor(...C.grisAcier);
  doc.setFontSize(7);
  doc.setFont(fontFamily, 'normal');
  // Show commercial contact info instead of generic email
  const parts = [];
  if (contact?.name) parts.push(contact.name);
  if (contact?.phone) parts.push(contact.phone);
  if (contact?.email) parts.push(contact.email);
  const footerContact = parts.length > 0 ? parts.join(' - ') : 'contact : energymanager@cockpit.placedesenergies.com';
  doc.text(footerContact, M, FOOTER_Y);
  doc.text(`${pageNum} / ${totalPages}`, RIGHT, FOOTER_Y, { align: 'right' });
};

// ── Text drawing helpers ─────────────────────────────────────────────────
const sectionTitle = (doc, text, y) => {
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text(text, M, y);
  // Blue underline
  doc.setDrawColor(...C.azur);
  doc.setLineWidth(0.8);
  doc.line(M, y + 1.5, M + doc.getTextWidth(text), y + 1.5);
  return y + 10; // more space after title
};

const bodyText = (doc, text, y, maxWidth) => {
  if (!text) return y;
  doc.setTextColor(...C.bleuPetroleDark);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'normal');
  const lines = doc.splitTextToSize(text, maxWidth || CONTENT_W);
  doc.text(lines, M, y);
  return y + lines.length * 3.8 + 2;
};

const labelValue = (doc, label, value, x, y) => {
  doc.setTextColor(...C.grisAcier);
  doc.setFontSize(7.5);
  doc.setFont(fontFamily, 'normal');
  doc.text(label, x, y);
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'bold');
  doc.text(String(value || '-'), x, y + 4.5);
};

// ── Draw load curve ──────────────────────────────────────────────────────
const drawLoadCurve = (doc, data, x, y, chartW, chartH, subscribedPower) => {
  if (!data?.values?.length) return y;
  const vals = data.values;
  const maxV = Math.max(...vals, subscribedPower || 0);
  const minV = Math.min(...vals);
  const range = maxV - minV || 1;
  const left = x + 14, right = x + chartW - 5;
  const top = y + 5, bottom = y + chartH - 14;
  const pW = right - left, pH = bottom - top;

  // Background
  doc.setFillColor(...C.blancLunaire);
  doc.rect(left, top, pW, pH, 'F');

  // Grid + Y labels
  doc.setDrawColor(...C.argent);
  doc.setLineWidth(0.12);
  for (let i = 0; i <= 4; i++) {
    const gy = top + (pH / 4) * i;
    doc.line(left, gy, right, gy);
    doc.setTextColor(...C.grisAcier);
    doc.setFontSize(6);
    doc.setFont(fontFamily, 'normal');
    doc.text(String(Math.round(maxV - (range / 4) * i)), left - 2, gy + 1.5, { align: 'right' });
  }
  doc.setTextColor(...C.grisAcier);
  doc.setFontSize(7);
  doc.text('kW', x + 3, top + pH / 2);

  // Plot line — peak-preserving downsampling (min/max per bucket)
  doc.setDrawColor(...C.azur);
  doc.setLineWidth(0.35);
  const targetPoints = 500;
  const bucketSize = Math.max(1, Math.floor(vals.length / targetPoints));
  const sampled = [];
  for (let b = 0; b < vals.length; b += bucketSize) {
    const end = Math.min(b + bucketSize, vals.length);
    let minIdx = b, maxIdx = b;
    for (let j = b; j < end; j++) {
      if (vals[j] < vals[minIdx]) minIdx = j;
      if (vals[j] > vals[maxIdx]) maxIdx = j;
    }
    // Add min and max in chronological order to preserve shape
    if (minIdx <= maxIdx) {
      sampled.push(minIdx);
      if (minIdx !== maxIdx) sampled.push(maxIdx);
    } else {
      sampled.push(maxIdx);
      if (minIdx !== maxIdx) sampled.push(minIdx);
    }
  }
  let px0 = null, py0 = null;
  for (const i of sampled) {
    const px = left + (i / (vals.length - 1)) * pW;
    const py = bottom - ((vals[i] - minV) / range) * pH;
    if (px0 !== null) doc.line(px0, py0, px, py);
    px0 = px; py0 = py;
  }

  // Subscribed power reference line
  if (subscribedPower && subscribedPower > minV) {
    const refY = bottom - ((subscribedPower - minV) / range) * pH;
    doc.setDrawColor(220, 50, 50); // red
    doc.setLineWidth(0.5);
    doc.setLineDashPattern([3, 2], 0);
    doc.line(left, refY, right, refY);
    doc.setLineDashPattern([], 0); // reset
    // Label
    doc.setTextColor(220, 50, 50);
    doc.setFontSize(7);
    doc.setFont(fontFamily, 'bold');
    doc.text(`Puissance souscrite : ${subscribedPower} kVA`, right, refY - 2, { align: 'right' });
  }

  // X labels (months)
  const mNames = ['Jan', 'F\u00E9v', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Ao\u00FB', 'Sep', 'Oct', 'Nov', 'D\u00E9c'];
  if (data.dates?.length) {
    const seen = new Set();
    doc.setTextColor(...C.grisAcier);
    doc.setFontSize(6);
    const xStep = Math.max(1, Math.floor(data.dates.length / 300));
    for (let i = 0; i < data.dates.length; i += xStep) {
      const d = new Date(data.dates[i]);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (!seen.has(key)) {
        seen.add(key);
        const px = left + (i / (data.dates.length - 1)) * pW;
        doc.text(mNames[d.getMonth()], px, bottom + 5, { align: 'center' });
      }
    }
  }

  // Caption
  doc.setFontSize(7);
  doc.setTextColor(...C.grisAcier);
  const caption = `Puissance (\u00C9lectricit\u00E9) - Compteur Enedis ${data.stats?.period || ''}`;
  doc.text(caption, x + chartW / 2, y + chartH - 3, { align: 'center' });

  return y + chartH + 2;
};

// ══════════════════════════════════════════════════════════════════════════
// MAIN EXPORT FUNCTION
// ══════════════════════════════════════════════════════════════════════════
export const generateOptimizationPDF = async (report) => {
  const { jsPDF } = await import('jspdf');
  const autoTableModule = await import('jspdf-autotable');
  const autoTable = autoTableModule.default;

  const doc = new jsPDF('p', 'mm', 'a4');
  await setupFont(doc);
  const logoBase64 = await loadLogo();

  // Calculate max subscribed power for load curve reference line
  const powers = report.current_powers || {};
  const maxSubscribedPower = Math.max(
    ...[powers.pointe, powers.hph, powers.hch, powers.hpe, powers.hce]
      .filter(v => v != null && !isNaN(v))
      .map(Number)
  ) || 0;

  // ════════════════════════════════════════════════════════════════════════
  // PAGE 1 — Introduction
  // ════════════════════════════════════════════════════════════════════════
  drawPageHeader(doc, logoBase64);
  let y = 40;

  // Client info card
  doc.setFillColor(...C.blancLunaire);
  doc.roundedRect(M, y, CONTENT_W, 30, 2, 2, 'F');

  labelValue(doc, "Charg\u00E9 d'affaires PDE :", report.contact?.name || '-', M + 4, y + 5);
  labelValue(doc, 'Client :', report.client_name || '-', M + 70, y + 5);
  labelValue(doc, 'PDL :', report.pdl || '-', M + 4, y + 17);
  const addr = report.address
    ? `${report.address.street || ''}, ${report.address.postal_code || ''} ${report.address.city || ''}`
    : '-';
  labelValue(doc, 'Adresse :', addr, M + 70, y + 17);
  if (report.report_date) {
    doc.setTextColor(...C.grisAcier);
    doc.setFontSize(7);
    doc.setFont(fontFamily, 'normal');
    doc.text(`Date : ${report.report_date}`, RIGHT - 4, y + 6, { align: 'right' });
  }
  y += 36;

  // Section: TURPE explanation
  y = sectionTitle(doc, "A quoi correspondent les frais d'acheminement sur ma facture d'\u00E9lectricit\u00E9 ?", y);
  y += 1;
  y = bodyText(doc,
    "Le Tarif d'Utilisation des R\u00E9seaux Publics d'\u00C9lectricit\u00E9 (TURPE) est pay\u00E9 par tous les consommateurs " +
    "d'\u00E9lectricit\u00E9. Il permet de r\u00E9mun\u00E9rer les Gestionnaires de R\u00E9seau de Transport (RTE) et du r\u00E9seau de " +
    "distribution (ENEDIS ou Entreprise Locale de Distribution).", y);
  y = bodyText(doc,
    "Son montant est d\u00E9termin\u00E9 par la Commission de R\u00E9gulation de l'\u00C9nergie en fonction des composantes " +
    "de comptage, de gestion et de soutirage.", y);
  y = bodyText(doc, "Le montant comprend :", y);

  // Bullet 1
  doc.setFillColor(...C.azur);
  doc.circle(M + 2, y + 1, 0.7, 'F');
  doc.setTextColor(...C.bleuPetroleDark);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'normal');
  const b1 = doc.splitTextToSize(
    "Une part fixe incluse dans le prix de l'abonnement mensuel, qui varie en fonction de la puissance souscrite en kVA, " +
    "et de la Formule Tarifaire d'Acheminement (FTA)", CONTENT_W - 8);
  doc.text(b1, M + 6, y + 2);
  y += b1.length * 3.8 + 4;

  // Bullet 2
  doc.setFillColor(...C.azur);
  doc.circle(M + 2, y + 1, 0.7, 'F');
  const b2 = doc.splitTextToSize(
    "Une part variable qui est factur\u00E9e sur la consommation en kWh selon la FTA.", CONTENT_W - 8);
  doc.text(b2, M + 6, y + 2);
  y += b2.length * 3.8 + 4;

  y = bodyText(doc,
    "La Contribution Tarifaire d'Acheminement (CTA) est une taxe qui finance les droits sp\u00E9cifiques de retraite des salari\u00E9s " +
    "des industries \u00E9lectriques et gazi\u00E8res. Elle est calcul\u00E9e sur la part fixe des co\u00FBts d'acheminement, donc " +
    "proportionnelle au TURPE.", y);

  y += 3;
  y = sectionTitle(doc, "Comment agir sur mes frais d'acheminement ?", y);
  y += 1;
  y = bodyText(doc,
    "Il est possible de r\u00E9duire les frais d'acheminement en optimisant la puissance souscrite par rapport \u00E0 la puissance " +
    "maximale r\u00E9ellement atteinte, en prenant en compte l'impact d'\u00E9ventuels d\u00E9passements entra\u00EEnant des " +
    "p\u00E9nalit\u00E9s.", y);

  // ════════════════════════════════════════════════════════════════════════
  // PAGE 2 — Situation actuelle
  // ════════════════════════════════════════════════════════════════════════
  doc.addPage();
  drawPageHeader(doc, logoBase64);
  y = 40;

  y = sectionTitle(doc, "Votre situation actuelle :", y);
  y += 1;
  doc.setTextColor(...C.bleuPetroleDark);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'normal');
  doc.text(`N\u00B0 de PDL : ${report.pdl || '-'}`, M, y);
  y += 5;
  doc.text(`Formule Tarifaire d'Acheminement : ${report.fta || '-'}`, M, y);
  y += 4;
  if (report.fta_description) {
    doc.setFontSize(8);
    doc.setTextColor(...C.grisAcier);
    doc.text(report.fta_description, M, y);
    y += 6;
  }

  // Current powers
  y += 2;
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text('Puissances souscrites actuelles :', M, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [PERIOD_LABELS],
    body: [PERIODS.map(p => report.current_powers?.[p] != null ? `${report.current_powers[p]} kVA` : '-')],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 3, halign: 'center', font: fontFamily },
    headStyles: { fillColor: C.azur, textColor: C.white, fontStyle: 'bold' },
    bodyStyles: { fillColor: C.blancLunaire },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 7;

  // Consumptions
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text("Consommations horosaisonni\u00E8res :", M, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [PERIOD_LABELS],
    body: [PERIODS.map(p => report.consumptions?.[p] != null ? `${fmtNum(report.consumptions[p])} kWh` : '-')],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 3, halign: 'center', font: fontFamily },
    headStyles: { fillColor: C.azur, textColor: C.white, fontStyle: 'bold' },
    bodyStyles: { fillColor: C.blancLunaire },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 7;

  // Current costs
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text("D\u00E9tails de vos co\u00FBts d'acheminement en euros :", M, y);
  y += 3;

  const currentRows = COST_ROWS.map(([label, key]) => [label, fmtEur(report.current_costs?.[key])]);
  currentRows.push(['Total', fmtEur(report.current_costs?.total)]);

  autoTable(doc, {
    startY: y,
    head: [['Poste', 'Actuel']],
    body: currentRows,
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: 2.5, font: fontFamily },
    headStyles: { fillColor: C.anthracite, textColor: C.white, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: C.blancLunaire },
    columnStyles: { 0: { cellWidth: 115 }, 1: { halign: 'right', fontStyle: 'bold', cellWidth: 59 } },
    margin: { left: M, right: M },
    didParseCell: (data) => {
      if (data.section === 'body' && data.row.index === currentRows.length - 1) {
        data.cell.styles.fillColor = C.azur;
        data.cell.styles.textColor = C.white;
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  // Small load curve if fits
  if (report.annual_load_curve_data?.values?.length && y < 195) {
    doc.setTextColor(...C.anthracite);
    doc.setFontSize(10);
    doc.setFont(fontFamily, 'bold');
    doc.text('Votre courbe de charge', M, y);
    y += 2;
    drawLoadCurve(doc, report.annual_load_curve_data, M, y, CONTENT_W, 55, maxSubscribedPower);
  }

  // ════════════════════════════════════════════════════════════════════════
  // PAGE 3 — Proposition d'optimisation
  // ════════════════════════════════════════════════════════════════════════
  doc.addPage();
  drawPageHeader(doc, logoBase64);
  y = 40;

  y = sectionTitle(doc, "Notre proposition d'optimisation :", y);
  y += 1;
  doc.setTextColor(...C.bleuPetroleDark);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'normal');
  doc.text(`N\u00B0 de PDL : ${report.pdl || '-'}`, M, y);
  y += 5;
  doc.text(`Formule Tarifaire d'Acheminement : ${report.fta || '-'}`, M, y);
  y += 4;
  if (report.fta_description) {
    doc.setFontSize(8);
    doc.setTextColor(...C.grisAcier);
    doc.text(report.fta_description, M, y);
    y += 6;
  }

  // Proposed powers
  y += 2;
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text("Puissances souscrites propos\u00E9es :", M, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [PERIOD_LABELS],
    body: [PERIODS.map(p => report.proposed_powers?.[p] != null ? `${report.proposed_powers[p]} kVA` : '-')],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 3, halign: 'center', font: fontFamily },
    headStyles: { fillColor: C.emeraude, textColor: C.white, fontStyle: 'bold' },
    bodyStyles: { fillColor: C.blancLunaire },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 7;

  // Consumptions (same)
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text("Consommations horosaisonni\u00E8res :", M, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [PERIOD_LABELS],
    body: [PERIODS.map(p => report.consumptions?.[p] != null ? `${fmtNum(report.consumptions[p])} kWh` : '-')],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 3, halign: 'center', font: fontFamily },
    headStyles: { fillColor: C.azur, textColor: C.white, fontStyle: 'bold' },
    bodyStyles: { fillColor: C.blancLunaire },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 7;

  // Comparison table
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text("D\u00E9tails de vos co\u00FBts d'acheminement en euros :", M, y);
  y += 3;

  const compRows = COST_ROWS.map(([label, key]) => [
    label,
    fmtEur(report.current_costs?.[key]),
    fmtEur(report.optimized_costs?.[key]),
    report.savings?.[key] ? fmtEur(report.savings[key]) : '-',
  ]);
  compRows.push([
    'Total',
    fmtEur(report.current_costs?.total),
    fmtEur(report.optimized_costs?.total),
    fmtEur(report.savings?.total),
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Poste', 'Actuel', 'Optimis\u00E9', '\u00C9conomie']],
    body: compRows,
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 2.5, font: fontFamily, overflow: 'linebreak' },
    headStyles: { fillColor: C.anthracite, textColor: C.white, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: C.blancLunaire },
    columnStyles: {
      0: { cellWidth: 75 },
      1: { halign: 'right', cellWidth: 33 },
      2: { halign: 'right', cellWidth: 33 },
      3: { halign: 'right', fontStyle: 'bold', cellWidth: 33 },
    },
    margin: { left: M, right: M },
    didParseCell: (data) => {
      if (data.section === 'body' && data.row.index === compRows.length - 1) {
        data.cell.styles.fillColor = C.azur;
        data.cell.styles.textColor = C.white;
        data.cell.styles.fontStyle = 'bold';
      }
      if (data.column.index === 3 && data.section === 'body' && data.row.index < compRows.length - 1) {
        const raw = data.cell.raw;
        if (raw && raw !== '-' && !raw.startsWith(`0,0`)) {
          data.cell.styles.textColor = C.emeraude;
        }
      }
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  // Commentary
  if (report.comment) {
    if (y > MAX_Y - 30) { doc.addPage(); drawPageHeader(doc, logoBase64); y = 40; }
    y = sectionTitle(doc, 'Commentaire :', y);
    y += 3;
    doc.setTextColor(...C.bleuPetroleDark);
    doc.setFontSize(10);
    doc.setFont(fontFamily, 'normal');
    // Render each paragraph justified to fill the full width
    const paragraphs = report.comment.split('\n').filter(p => p.trim());
    paragraphs.forEach(p => {
      const lines = doc.splitTextToSize(p.trim(), CONTENT_W);
      // Justify all lines except the last one of each paragraph
      lines.forEach((line, i) => {
        if (i < lines.length - 1) {
          doc.text(line, M, y, { maxWidth: CONTENT_W, align: 'justify' });
        } else {
          doc.text(line, M, y); // last line left-aligned
        }
        y += 4.5;
      });
      y += 3; // paragraph spacing
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // PAGE 4 — Conditions de validit\u00E9 & Questionnaire
  // ════════════════════════════════════════════════════════════════════════
  doc.addPage();
  drawPageHeader(doc, logoBase64);
  y = 40;

  y = sectionTitle(doc, "Rappel : Conditions de validit\u00E9 de l'Optimisation", y);
  y += 1;
  y = bodyText(doc,
    "La proposition d'Optimisation de Puissance Souscrite r\u00E9alis\u00E9e dans ce rapport est bas\u00E9e sur le " +
    "comportement \u00E9lectrique des 12 derniers mois.", y);
  y = bodyText(doc,
    "Si un changement est apport\u00E9 sur le comportement, Place des \u00C9nergies ne pourra \u00EAtre tenu " +
    "responsable de la non-atteinte des gains estim\u00E9s dans ce document.", y);
  y = bodyText(doc,
    "Le risque le plus fr\u00E9quent \u00E9tant la pr\u00E9sence de d\u00E9passements de puissance souscrite trop importants.", y);
  y += 2;

  doc.setTextColor(...C.anthracite);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'bold');
  const qIntro = doc.splitTextToSize(
    "Merci de veiller \u00E0 nous informer si vous avez connaissance de changements en r\u00E9pondant au questionnaire suivant :",
    CONTENT_W);
  doc.text(qIntro, M, y);
  y += qIntro.length * 3.8 + 6;

  // Questionnaire
  const questions = [
    "La consommation de r\u00E9f\u00E9rence utilis\u00E9e pour cette \u00E9tude est-elle incoh\u00E9rente par rapport \u00E0 votre activit\u00E9 ?",
    "Des travaux pouvant impacter la consommation \u00E9lectrique du PDL ont-ils \u00E9t\u00E9 r\u00E9alis\u00E9s sur les 12 derniers mois ?",
    "Des travaux pouvant impacter la consommation \u00E9lectrique du PDL sont-ils pr\u00E9vus sur les 12 prochains mois ?",
    "\u00C0 votre connaissance, la consommation du PDL est-elle impact\u00E9e par de l'\u00E9nergie r\u00E9active venant d'\u00E9quipement du type Moteurs, Transformateurs ou Tubes fluorescents ?",
  ];

  questions.forEach((q, i) => {
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(9);
    const qLines = doc.splitTextToSize(q, CONTENT_W - 52);
    const qH = Math.max(qLines.length * 3.8 + 6, 14);

    // Card
    doc.setFillColor(...C.blancLunaire);
    doc.roundedRect(M, y, CONTENT_W, qH, 1.5, 1.5, 'F');

    doc.setTextColor(...C.anthracite);
    doc.text(`${i + 1}.`, M + 3, y + 6);
    doc.text(qLines, M + 10, y + 6);

    // OUI / NON
    const bx = RIGHT - 40;
    doc.setDrawColor(...C.argent);
    doc.setLineWidth(0.35);
    doc.roundedRect(bx, y + 2, 16, 8, 1, 1, 'S');
    doc.roundedRect(bx + 19, y + 2, 16, 8, 1, 1, 'S');
    doc.setTextColor(...C.grisAcier);
    doc.setFontSize(7);
    doc.setFont(fontFamily, 'bold');
    doc.text('OUI', bx + 8, y + 7.5, { align: 'center' });
    doc.text('NON', bx + 27, y + 7.5, { align: 'center' });

    y += qH + 3;
  });

  y += 4;
  y = bodyText(doc,
    "Enfin, il est important de savoir qu'une baisse de Puissance Souscrite peut se r\u00E9p\u00E9ter. " +
    "Cependant, s'il y a augmentation, celle-ci ne peut se faire qu'une fois par an \u00E0 condition qu'aucune " +
    "diminution n'ait \u00E9t\u00E9 demand\u00E9e lors des 12 derniers mois. Dans le cas contraire, ENEDIS facture des " +
    "p\u00E9nalit\u00E9s.", y);

  // ════════════════════════════════════════════════════════════════════════
  // PAGE 5 — Conclusion & Mise en place
  // ════════════════════════════════════════════════════════════════════════
  doc.addPage();
  drawPageHeader(doc, logoBase64);
  y = 40;

  y = sectionTitle(doc, 'CONCLUSION', y);
  y += 1;
  y = bodyText(doc,
    "Si toutes les r\u00E9ponses au questionnaire pr\u00E9c\u00E9dent \u00E9taient n\u00E9gatives, alors la proposition " +
    "d'optimisation de puissance souscrite semble adapt\u00E9e \u00E0 vos besoins.", y);
  y = bodyText(doc,
    "Si une seule r\u00E9ponse \u00E9tait positive, une analyse un peu plus pouss\u00E9e doit \u00EAtre men\u00E9e pour " +
    "\u00E9liminer le risque de d\u00E9passement et vous permettre de r\u00E9aliser un maximum d'\u00E9conomie sur vos " +
    "co\u00FBts d'acheminement.", y);
  y = bodyText(doc,
    "Enfin, il est important de savoir qu'une baisse de Puissance Souscrite peut se r\u00E9p\u00E9ter. " +
    "Cependant, s'il y a augmentation, celle-ci ne peut se faire qu'une fois par an \u00E0 condition qu'aucune " +
    "diminution n'ait \u00E9t\u00E9 demand\u00E9e lors des 12 derniers mois. Dans le cas contraire, ENEDIS facture des " +
    "p\u00E9nalit\u00E9s.", y);

  y += 5;
  y = sectionTitle(doc, "MISE EN PLACE DE L'OPTIMISATION", y);
  y += 1;
  y = bodyText(doc,
    "La mise en place de l'Optimisation des co\u00FBts d'acheminement r\u00E9side dans un simple message \u00E0 " +
    "transmettre \u00E0 votre fournisseur d'\u00E9lectricit\u00E9 dans l'espace client.", y);

  y += 1;
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'bold');
  doc.text("Voici un template de message \u00E0 adresser :", M, y);
  y += 6;

  // Template box — calculate height dynamically
  const tmplBody = doc.splitTextToSize(
    "Apr\u00E8s analyse de mes consommations, je souhaite modifier la puissance souscrite de mon compteur " +
    "pour l'adapter \u00E0 mes besoins r\u00E9els. Merci de la modifier avec les informations suivantes :",
    CONTENT_W - 14);
  const boxH = 54 + tmplBody.length * 3.8;

  doc.setFillColor(...C.blancLunaire);
  doc.setDrawColor(...C.azur);
  doc.setLineWidth(0.5);
  doc.roundedRect(M, y, CONTENT_W, boxH, 2, 2, 'FD');

  doc.setTextColor(...C.anthracite);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'bold');
  doc.text('Objet : Modification de la puissance souscrite', M + 6, y + 8);
  doc.setFont(fontFamily, 'normal');
  doc.setTextColor(...C.bleuPetroleDark);
  doc.text('Madame, Monsieur,', M + 6, y + 15);
  doc.text(tmplBody, M + 6, y + 21);

  let tly = y + 21 + tmplBody.length * 3.8 + 4;
  doc.setTextColor(...C.anthracite);
  doc.setFont(fontFamily, 'bold');
  doc.text(`PDL : ${report.pdl || '-'}`, M + 6, tly);
  tly += 5;
  doc.text(`FTA : ${report.fta || '-'}`, M + 6, tly);
  tly += 5;

  // Powers mini-table inside box
  autoTable(doc, {
    startY: tly,
    head: [PERIOD_LABELS],
    body: [PERIODS.map(p => `${report.proposed_powers?.[p] ?? '-'} kVA`)],
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 2, halign: 'center', font: fontFamily },
    headStyles: { fillColor: C.azur, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fillColor: [255, 255, 255] },
    margin: { left: M + 6, right: M + 6 },
  });

  // ════════════════════════════════════════════════════════════════════════
  // PAGE 6 — Courbe de charge compl\u00E8te (if data)
  // ════════════════════════════════════════════════════════════════════════
  if (report.annual_load_curve_data?.values?.length) {
    doc.addPage();
    drawPageHeader(doc, logoBase64);
    y = 40;
    doc.setTextColor(...C.anthracite);
    doc.setFontSize(11);
    doc.setFont(fontFamily, 'bold');
    doc.text('Courbe de charge annuelle', M, y);
    y += 4;
    drawLoadCurve(doc, report.annual_load_curve_data, M, y, CONTENT_W, 200, maxSubscribedPower);
  }

  // ════════════════════════════════════════════════════════════════════════
  // PAGE 7 — Contact
  // ════════════════════════════════════════════════════════════════════════
  doc.addPage();
  drawPageHeader(doc, logoBase64);
  y = 40;

  y = sectionTitle(doc, 'Nous contacter :', y);
  y += 3;

  // Advisor card
  doc.setFillColor(...C.blancLunaire);
  doc.roundedRect(M, y, CONTENT_W, 48, 3, 3, 'F');
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text('Votre conseiller :', M + 8, y + 12);
  doc.setFontSize(10);
  doc.setTextColor(...C.bleuPetroleDark);
  let cy = y + 20;
  if (report.contact?.name) {
    doc.setFont(fontFamily, 'bold');
    doc.text(report.contact.name, M + 8, cy);
    doc.setFont(fontFamily, 'normal');
    cy += 6;
  }
  if (report.contact?.phone) { doc.text(`T\u00E9l\u00E9phone : ${report.contact.phone}`, M + 8, cy); cy += 6; }
  if (report.contact?.email) { doc.text(`E-mail : ${report.contact.email}`, M + 8, cy); cy += 6; }
  if (report.contact?.office_address) {
    const rawCity = (report.contact.office_city || '').replace(/[\n\r]/g, ' ').replace(/\s+/g, ' ').trim();
    // Strip trailing single character artifacts from parsing
    const city = rawCity.replace(/\s+[A-Z]$/, '').trim();
    doc.text(`Adresse : ${report.contact.office_address}${city ? ', ' + city : ''}`, M + 8, cy);
  }
  y += 56;

  // Company card - Agency info
  doc.setFillColor(...C.blancLunaire);
  doc.roundedRect(M, y, CONTENT_W, 30, 3, 3, 'F');
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text('PLACE DES ENERGIES', M + 8, y + 12);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'normal');
  doc.setTextColor(...C.bleuPetroleDark);
  const agencyName = report.agency?.name;
  const agencyAddr = report.agency?.address || '33 Avenue Auguste Ferret';
  const agencyCity = report.agency?.city || '33110 LE BOUSCAT';
  if (agencyName) {
    doc.text(agencyName, M + 8, y + 20);
    doc.text(`${agencyAddr}, ${agencyCity}`, M + 8, y + 26);
  } else {
    doc.text(`Adresse : ${agencyAddr}, ${agencyCity}`, M + 8, y + 20);
    doc.text('www.placedesenergies.com', M + 8, y + 26);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Apply footers to ALL pages
  // ════════════════════════════════════════════════════════════════════════
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawPageFooter(doc, p, totalPages, report.contact);
  }

  // ── Save ────────────────────────────────────────────────────────────────
  const slug = (report.client_name || 'rapport').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
  doc.save(`Rapport_Optim_${slug}.pdf`);
};
